// ManageUserDetail.jsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import VerificationModal from "../../Modals/verificationModal";

/**
 * Fitness version of ManageUserDetail
 * - Keeps your original classes and layout
 * - Replaces order/loyalty data with fitness data
 * - Safely reads user from location.state.user (fallbacks provided)
 *
 * File: src/pages/manage-users/ManageUserDetail.jsx
 */

interface CustomTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

const CustomTabPanel: React.FC<CustomTabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const ManageUserDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // read user from location.state if passed from UserList
  const passedUser = location?.state?.user ?? null;

  // SAFE MOCK user used when no state passed
  const mockUser = {
    id: 1,
    name: "Madelyn Dias",
    email: "madelyndias@gmail.com",
    phone: "+91 9876543210",
    nationality: "Indian",
    joinedAt: "01 March 2025",
    dob: "01 March 2000",
    memberId: "MA-40568476",
    status: "Active",
    subscription: {
      plan: "Monthly",
      startDate: "2025-10-15",
      endDate: "2025-11-14",
      autoRenew: true,
      paymentStatus: "Paid",
      amount: 999,
      history: [
        { id: "TXN987654", date: "2025-09-15", plan: "Monthly", amount: 999 },
      ],
    },
    stats: {
      totalWorkouts: 125,
      lastWorkoutDate: "2025-11-19",
      weeklyAvg: 4,
      caloriesWeek: 2400,
      points: 1350,
      badges: ["7-day Streak", "Cardio Pro"],
    },
    workoutHistory: [
      { id: "W_1001", date: "2025-11-19", program: "Strength 8 Weeks", duration: 38, calories: 420, status: "Completed" },
      { id: "W_1000", date: "2025-11-17", program: "Cardio Boost", duration: 30, calories: 320, status: "Completed" },
    ],
    achievements: [
      { id: "A1", title: "7-Day Streak", date: "2025-10-22" },
      { id: "A2", title: "100 Workouts", date: "2025-11-01" },
    ],
    community: [
      { id: 1, date: "2025-11-15", text: "Loved today's HIIT!", type: "post" },
      { id: 2, date: "2025-11-12", text: "Shared a tip in 'Leg Day' thread", type: "comment" },
    ],
    loginHistory: [
      { id: 1, date: "2025-11-20 17:12", device: "Web" },
      { id: 2, date: "2025-11-19 07:18", device: "Android" },
    ],
  };

  const user = passedUser ?? mockUser;

  // tabs
  const [valueTabs, setValueTabs] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const handleCloseModal = () => setOpenModal(false);

  // tab labels for the top (fitness modules per your choice B)
  // we'll show: Personal Info, Subscription, Workouts, Progress, Achievements, Community
  const topTabs = ["Personal Info", "Subscription", "Workouts", "Progress", "Achievements", "Community"];

  return (
    <>
      <div className="main_title">
        <h1>User Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>Manage Users</button> - User Detail
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs value={valueTabs} onChange={handleChangeTabs} className="custom_tabs_links" variant="scrollable" scrollButtons="auto">
              {topTabs.map((t, i) => (
                <Tab key={i} label={t} {...a11yProps(i)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      {/* ---------- PERSONAL INFO ---------- */}
      <CustomTabPanel value={valueTabs} index={0}>
        <div className="cards view_page">
          <div className="table_header">
            <h2>Personal Info</h2>
            <div className="right_s">
              <Button className="btn btn_primary sm" onClick={() => setOpenModal(true)}>
                Resend Verification
              </Button>
            </div>
          </div>

          <div className="gap_p">
            <div className="w_33">
              <h6>Full Name</h6>
              <p>{user?.name ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Email</h6>
              <p>{user?.email ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Phone</h6>
              <p>
                {user?.phone ?? "-"}{" "}
                {/* optionally show verified tick if your data has it */}
              </p>
            </div>

            <div className="w_33">
              <h6>Nationality</h6>
              <p>{user?.nationality ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Date Registered</h6>
              <p>{user?.joinedAt ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>DOB</h6>
              <p>{user?.dob ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Member Account</h6>
              <p>{user?.memberId ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Status</h6>
              <p>{user?.status ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Last Active</h6>
              <p>{user?.lastActive ?? "-"}</p>
            </div>
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- SUBSCRIPTION ---------- */}
      <CustomTabPanel value={valueTabs} index={1}>
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <div className="section_title">
                <h2>
                  Subscription Details - <span className="c_primary">{user?.subscription?.plan ?? "—"}</span>
                </h2>
              </div>
            </div>
            <div className="right_s">
              <Button className="btn btn_secondary sm" onClick={() => { /* open change plan modal */ }}>
                Change Plan
              </Button>
            </div>
          </div>

          <div className="gap_p">
            <div className="w_33">
              <h6>Current Plan</h6>
              <p>{user?.subscription?.plan ?? "—"}</p>
            </div>
            <div className="w_33">
              <h6>Start Date</h6>
              <p>{user?.subscription?.startDate ?? "—"}</p>
            </div>
            <div className="w_33">
              <h6>End Date</h6>
              <p>{user?.subscription?.endDate ?? "—"}</p>
            </div>
            <div className="w_33">
              <h6>Auto Renew</h6>
              <p>{user?.subscription?.autoRenew ? "On" : "Off"}</p>
            </div>
            <div className="w_33">
              <h6>Payment Status</h6>
              <p>{user?.subscription?.paymentStatus ?? "—"}</p>
            </div>
            <div className="w_33">
              <h6>Amount</h6>
              <p>₹{user?.subscription?.amount ?? 0}</p>
            </div>
          </div>

          <div className="cards mt_20">
            <h6>Subscription History</h6>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Transaction ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(user?.subscription?.history ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>No subscription history available</TableCell>
                    </TableRow>
                  ) : (
                    (user.subscription.history || []).map((h: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{h.date}</TableCell>
                        <TableCell>{h.plan}</TableCell>
                        <TableCell>₹{h.amount}</TableCell>
                        <TableCell>{h.id}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- WORKOUT HISTORY ---------- */}
      <CustomTabPanel value={valueTabs} index={2}>
        <div className="cards">
          <div className="table_header">
            <h2>Workout History</h2>
            <div className="right_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                placeholder="Search workouts..."
              />
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Duration (min)</TableCell>
                  <TableCell>Calories</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(user?.workoutHistory ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>No workouts found</TableCell>
                  </TableRow>
                ) : (
                    (user.workoutHistory || []).map((w: any) => (
                    <TableRow hover key={w.id}>
                      <TableCell>{w.id}</TableCell>
                      <TableCell>{w.date}</TableCell>
                      <TableCell>{w.program}</TableCell>
                      <TableCell>{w.duration}</TableCell>
                      <TableCell>{w.calories}</TableCell>
                      <TableCell>{w.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </CustomTabPanel>

      {/* ---------- PROGRESS ---------- */}
      <CustomTabPanel value={valueTabs} index={3}>
        <div className="cards">
          <div className="table_header">
            <h2>Progress Tracking</h2>
          </div>

          <div className="gap_p">
            <div className="w_33">
              <h6>Total Workouts</h6>
              <p>{user?.stats?.totalWorkouts ?? 0}</p>
            </div>
            <div className="w_33">
              <h6>Weekly Avg</h6>
              <p>{user?.stats?.weeklyAvg ?? 0} / week</p>
            </div>
            <div className="w_33">
              <h6>Last Workout</h6>
              <p>{user?.stats?.lastWorkoutDate ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Calories (week)</h6>
              <p>{user?.stats?.caloriesWeek ?? "-"}</p>
            </div>
            <div className="w_33">
              <h6>Points</h6>
              <p>{user?.stats?.points ?? 0}</p>
            </div>
            <div className="w_33">
              <h6>Badges</h6>
              <p>{(user?.stats?.badges || []).join(", ") || "-"}</p>
            </div>
          </div>

          <div className="mt_20">
            <h6>Program Completion</h6>
            {(user?.programs ?? []).length === 0 ? (
              <p>No programs enrolled</p>
            ) : (
              (user.programs || []).map((p: any, idx: number) => (
                <div key={idx} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>{p.name} • {p.level}</strong>
                    <span>{p.completion}%</span>
                  </div>
                  <div style={{ height: 8, background: "#f0f0f0", borderRadius: 6, marginTop: 6 }}>
                    <div style={{ width: `${p.completion}%`, height: "100%", background: "#1976d2", borderRadius: 6 }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- ACHIEVEMENTS ---------- */}
      <CustomTabPanel value={valueTabs} index={4}>
        <div className="cards">
          <div className="table_header">
            <h2>Achievements</h2>
          </div>

          <div className="gap_p">
            {(user?.achievements ?? []).length === 0 ? (
              <p>No achievements yet</p>
            ) : (
              (user.achievements || []).map((a: any) => (
                <div className="w_33" key={a.id}>
                  <h6>{a.title}</h6>
                  <p>{a.date}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- COMMUNITY ---------- */}
      <CustomTabPanel value={valueTabs} index={5}>
        <div className="cards">
          <div className="table_header">
            <h2>Community Activity</h2>
            <div className="right_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                placeholder="Search community..."
              />
            </div>
          </div>

          <div className="gap_p">
            {(user?.community ?? []).length === 0 ? (
              <p>No community posts/comments</p>
            ) : (
              (user.community || []).map((c: any) => (
                <div key={c.id} className="w_100" style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>{c.type === "post" ? "Post" : "Comment"}</strong>
                    <span>{c.date}</span>
                  </div>
                  <p>{c.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </CustomTabPanel>

      <VerificationModal open={openModal} onClose={handleCloseModal} setOpen={setOpenModal} />
    </>
  );
};

export default ManageUserDetail;
